import styled from "@emotion/styled";
import dynamic from "next/dynamic";
import React from "react";

import { useInput } from "../hooks";
import { DataType, RenderFieldProps, Field } from "../types";

import { CheckboxList } from "./CheckboxList";

// Dynamic imports to avoid SSR issues with component library
const TextInput = dynamic(() => import("./component-library/TextInput"), {
  ssr: false,
});

const MultiLineTextInput = dynamic(
  () => import("./component-library/MultiLineTextInput"),
  { ssr: false },
);

const OptionInput = dynamic(() => import("./component-library/OptionInput"), {
  ssr: false,
});

const PhoneInput = dynamic(() => import("./component-library/PhoneInput"), {
  ssr: false,
});

const CurrencyInput = dynamic(
  () => import("./component-library/CurrencyInput"),
  { ssr: false },
);

const InlineEmailInputWrapper = styled.div({
  flex: 1,
  "& *": {
    border: "none",
    borderColor: "transparent !important",
    padding: 0,
    boxShadow: "none !important",
    "&:focus": {
      outline: "none",
    },
    "&:invalid": {
      boxShadow: "none",
    },
  },
});

export const FormField: React.FC<RenderFieldProps> = ({
  field,
  setFields,
  submitAttempted,
  formId,
  isInlineEmail,
}) => {
  // inputMode?: "text" | "email" | "none" | "tel" | "url" | "numeric" | "decimal" | "search" | undefined

  const { onInputChange, onInputBlur, onInputFocus, onCheckboxChange } =
    useInput(setFields, formId, field);

  switch (field.dataType) {
    //build them all
    case DataType.CURRENCY:
      return (
        <CurrencyInput
          size="large"
          name={field.name}
          value={field.value}
          label={field.label}
          error={submitAttempted && field.error}
          onChange={onInputChange}
          onBlur={onInputBlur}
          onFocus={onInputFocus}
        />
      );
    case DataType.TELEPHONE:
      return (
        <PhoneInput
          size="large"
          name={field.name}
          placeholder={field.placeholder}
          value={field.value}
          label={field.label}
          error={submitAttempted && field.error}
          onChange={onInputChange}
          onBlur={onInputBlur}
          onFocus={onInputFocus}
        />
      );

    case DataType.URL:
      return (
        <TextInput
          size="large"
          inputMode="url"
          name={field.name}
          placeholder={field.placeholder}
          value={field.value}
          label={field.label}
          error={submitAttempted && field.error}
          onChange={onInputChange}
          onBlur={onInputBlur}
          onFocus={onInputFocus}
        />
      );

    case DataType.TEXT:
      return (
        <TextInput
          size="large"
          inputMode="text"
          name={field.name}
          placeholder={field.placeholder}
          value={field.value}
          label={field.label}
          error={submitAttempted && field.error}
          onChange={onInputChange}
          onBlur={onInputBlur}
          onFocus={onInputFocus}
          maxLength={field?.maxLength ?? 255}
        />
      );

    case DataType.EMAIL:
      if (isInlineEmail) {
        return (
          <InlineEmailInputWrapper>
            <TextInput
              size="large"
              inputMode="email"
              name={field.name}
              placeholder={field.placeholder}
              value={field.value}
              label={field.label}
              error={submitAttempted && field.error}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onFocus={onInputFocus}
            />
          </InlineEmailInputWrapper>
        );
      }
      return (
        <TextInput
          size="large"
          inputMode="email"
          name={field.name}
          placeholder={field.placeholder}
          value={field.value}
          label={field.label}
          error={submitAttempted && field.error}
          onChange={onInputChange}
          onBlur={onInputBlur}
          onFocus={onInputFocus}
        />
      );

    case DataType.TEXTAREA:
      return (
        <MultiLineTextInput
          size="large"
          name={field.name}
          value={field.value}
          placeholder={field.placeholder}
          label={field.label}
          error={submitAttempted && field.error}
          onChange={onInputChange}
          onBlur={onInputBlur}
          onFocus={onInputFocus}
          minHeight={100}
          maxLength={field?.maxLength ?? 10000}
        />
      );

    case DataType.SELECT:
    case DataType.RADIOBUTTONS:
      return (
        <OptionInput
          name={field.name}
          value={field.value}
          label={field.label}
          error={submitAttempted && field.error}
          options={field.options}
          onChange={onInputChange}
          onBlur={onInputBlur}
          onFocus={onInputFocus}
        />
      );

    case DataType.CHECKBOXES:
      return (
        <CheckboxList
          name={field.name}
          options={field.options}
          value={field.value}
          label={field.label}
          error={submitAttempted && field.error}
          onChange={onCheckboxChange}
        />
      );

    case DataType.HIDDEN:
      return null;

    default:
      throw new Error(
        "Invalid or unsupported input type: " + (field as Field).dataType,
      );
  }
};
